import { gql } from '@apollo/client';
import { Badge, Button, Divider, Space } from 'antd';
import { useMutation } from 'hooks/apollo';
import React, { useCallback, useMemo, useState } from 'react';
import { BarChartOutlined, DeleteFilled, EditOutlined, EyeOutlined, PlusOutlined, StopOutlined, ToTopOutlined, UpCircleOutlined } from '@ant-design/icons';
import { CREATE_SURVEY, EDIT_SURVEY } from 'shared/constants/permissions';
import theme from 'shared/constants/theme.json';
import useDataSource from 'hooks/useDataSource';
import { useHistory, Link, generatePath, useRouteMatch } from 'react-router-dom';
import { sum } from 'shared/constants/math';
import { dayjs } from 'shared/utils/date';
import { Module, SurveyType } from 'shared/prisma/types';
import { MODULE_LABEL, SURVEY_LIMIT_TYPES, SURVEY_TYPE_LIMITS } from 'shared/constants/settings';
import useGlobalContext from 'hooks/useGlobalContext';
import { useSurveyPreviewPopup } from 'components/SurveyPreview';
import { DISTRIBUTION_STATUS } from 'shared/constants/distribution';
import { FormattedMessage } from 'react-intl';
import globalMessages from 'translations/messages';
import LanguageTag from 'components/LanguageTag';
import { arraySorter, boolSorter } from 'components/Table/sorters';
import SurveyTypeTag from 'components/SurveyTypeTag';
import SurveyGroupStatusTag from 'components/SurveyGroupStatusTag';
import { getDistributionMethodsColumn } from 'components/SurveyDistributionTag';
import Table from 'components/Table';
import Page from 'components/Page';
import SurveyTypePluralLabel from 'components/SurveyTypePluralLabel';
import { MODULE_FROM_PATH } from 'shared/constants/survey';
import LicenseTag from 'components/LicenseTag';
import _ from 'lodash';
import useUser from 'hooks/useUser';
import { uniqueArray } from 'shared/utils/unique';
import { renderVersion } from 'components/Table/renderers';
import { hasLicenseLimit } from 'shared/utils/license';
import { getDistributionMethods } from 'shared/utils/distribution';
import CreateSurvey from './CreateSurvey';
import MigrateSurvey from './MigrateSurvey';
import RenameSurvey from './RenameSurvey';
import AddTranslations from './AddTranslations';

const extraFields = `
  surveyGroups(statsDateRanges:[null]) {
    id
    title
    surveys {
      id
      type
      title
      version
      primary
      language
      languageName
      viewCounts
      responseCounts
      firstResponse
      lastResponse
      distributions {
        id
        startDate
        endDate
        deviceTypes
        popupWidth
        popupHeight
        behaviorSetting
        container
        trigger
      }
    }
  }
`;

const CREATE_SURVEY_VERSION = gql`
  mutation Surveys_createSurveyVersion($id: ID!) {
    createSurveyVersion(id: $id)
  }
`;

const MAKE_PRIMARY = gql`
  mutation Surveys_makePrimary($id: ID!) {
    makeSurveyPrimary(id: $id)
  }
`;

const DELETE_SURVEY = gql`
  mutation DataSource_deleteSurvey($id: ID!) {
    deleteSurvey(id: $id)
  }
`;

const STOP_SURVEY = gql`
  mutation DataSource_stopSurvey($id: ID!) {
    stopSurvey(id: $id) {
      id
      status
    }
  }
`;

const SurveyLimitBadge = ({ limit, count }) => {
  let color = 'success-color';
  if (hasLicenseLimit(limit)) {
    if (count > limit) {
      color = 'error-color';
    } else if (count === limit) {
      color = 'warning-color';
    }
  }
  return (
    <Badge style={{ backgroundColor: theme[color] }} count={`${count || 0} / ${limit >= 0 ? limit : '∞'}`} showZero />
  );
};

const mapSurvey = (survey, group) => {
  const { id, title, distributions, language, languageName, viewCounts, responseCounts, firstResponse, lastResponse, primary, type } = survey;
  const [viewCount] = viewCounts;
  const [responseCount] = responseCounts;

  let version;

  if (survey.version) {
    version = primary ? 'Primary' : 'Translation';
  }

  return {
    id,
    title,
    type,
    surveyId: survey.id,
    groupId: group.id,
    version: version || null,
    languages: [{ language, languageName }],
    viewCount: viewCount || null,
    responseCount: responseCount || null,
    firstResponse: firstResponse && dayjs(firstResponse),
    lastResponse: lastResponse && dayjs(lastResponse),
    distributions,
    distributionMethods: getDistributionMethods(distributions, type),
  };
};

const mapSurveyVersion = (survey, group) => {
  const { title, version } = survey;

  const surveys = group.surveys.filter(s => s.version === version).sort((a, b) => boolSorter(a.primary, b.primary)).map(s => mapSurvey(s, group));

  return {
    id: `primary-${survey.id}`,
    title,
    type: survey.type,
    surveyId: survey.id,
    groupId: group.id,
    version: version ? `v${version}` : null,
    primary: !!survey.primary,
    languages: uniqueArray(surveys.flatMap(s => s.languages), { prop: 'language' }).sort((a, b) => a.languageName?.localeCompare(b.languageName)),
    viewCount: sum(surveys, { valueSelector: s => s.viewCount }) || null,
    responseCount: sum(surveys, { valueSelector: s => s.responseCount }) || null,
    firstResponse: surveys.reduce((d, s) => (!d || s.firstResponse?.isBefore(d) ? s.firstResponse : d), null),
    lastResponse: surveys.reduce((d, s) => (!d || s.lastResponse?.isAfter(d) ? s.lastResponse : d), null),
    distributions: surveys.flatMap(s => s.distributions),
    distributionMethods: getDistributionMethods(surveys.flatMap(s => s.distributions), survey.type),
    children: surveys.length > 1 ? surveys : null,
  };
};

const mapSurveyGroup = (group) => {
  const surveys = [
    ..._.uniqBy([
      ...group.surveys.filter(s => s.version && s.primary),
      ...group.surveys.filter(s => s.version),
    ], 'version').map(s => mapSurveyVersion(s, group)),
    ...group.surveys.filter(s => !s.version).map(s => mapSurvey(s, group)),
  ];

  let children;

  if (surveys.length > 1) {
    children = surveys;
  } else if (surveys.length === 1) {
    children = surveys[0].children;
  }

  return {
    id: group.id,
    surveyId: surveys.length > 0 ? surveys[surveys.length - 1].surveyId : null,
    groupId: group.id,
    versions: _.uniq(surveys.map(s => s.version).filter(v => v)),
    primary: !!surveys.find(s => s.primary),
    title: group.title,
    type: group.surveys.length > 0 ? group.surveys[0].type : null,
    languages: uniqueArray(surveys.flatMap(s => s.languages), { prop: 'language' }).sort((a, b) => a.languageName?.localeCompare(b.languageName)),
    viewCount: sum(surveys, { valueSelector: s => s.viewCount }) || null,
    responseCount: sum(surveys, { valueSelector: s => s.responseCount }) || null,
    firstResponse: surveys.reduce((d, s) => (!d || s.firstResponse?.isBefore(d) ? s.firstResponse : d), null),
    lastResponse: surveys.reduce((d, s) => (!d || s.lastResponse?.isAfter(d) ? s.lastResponse : d), null),
    distributions: surveys.flatMap(s => s.distributions),
    distributionMethods: getDistributionMethods(surveys.flatMap(s => s.distributions), group.surveys[0]?.type),
    children,
    group: true,
  };
};

const surveyPath = '/data-sources/:dataSourceId/:module/:page/:surveyGroupId/:displayedSurveyId';

const SurveyList = () => {
  const { isSuperUser } = useUser();
  const { params } = useRouteMatch('/data-sources/:dataSourceId/:module');
  const module = MODULE_FROM_PATH[params.module];
  const { dataSource, hasDataSourcePermission, getDataSourceLicenseSetting, refetch, loading } = useDataSource({ includeLicenseUsage: true, extraFields });
  const [renameSurveyVersion, setRenameSurveyVersion] = useState(null);
  const [createSurveyVersion, { loading: createVersionLoading }] = useMutation(CREATE_SURVEY_VERSION);
  const [makePrimary, { loading: makePrimaryLoading }] = useMutation(MAKE_PRIMARY);
  const [deleteSurvey, { loading: deleteSurveyLoading }] = useMutation(DELETE_SURVEY);
  const [stopSurvey, { loading: stopSurveyLoading }] = useMutation(STOP_SURVEY);
  const [createSurvey, setCreateSurvey] = useState(false);
  const [migrateSurvey, setMigrateSurvey] = useState(false);
  const [addTranslationId, setAddTranslationId] = useState(null);
  const { message, modal } = useGlobalContext();
  const surveyPreviewPopup = useSurveyPreviewPopup();
  const canEdit = hasDataSourcePermission(EDIT_SURVEY);
  const history = useHistory();
  const surveys = useMemo(() => dataSource?.surveyGroups.map(group => mapSurveyGroup(group)), [dataSource]);

  const onDeleteSurvey = async (id) => {
    modal.confirm({
      title: 'Are you sure you want to delete this survey?',
      okText: 'Delete',
      okType: 'danger',
      confirmLoading: true,
      onOk: async () => {
        try {
          await deleteSurvey({ variables: { id } });
          refetch();
        } catch (e) {
          message.error(e.message);
        }
      },
    });
  };

  const onCreateVersion = async (id) => {
    try {
      await createSurveyVersion({ variables: { id } });
      refetch();
    } catch (e) {
      message.error(e.message);
    }
  };

  const onMakePrimary = async (id) => {
    try {
      await makePrimary({ variables: { id } });
      refetch();
    } catch (e) {
      message.error(e.message);
    }
  };

  const onEditDone = () => {
    setMigrateSurvey(false);
    setAddTranslationId(false);
    refetch();
  };

  const getEditLink = record => generatePath(surveyPath, { ...params, page: 'edit', surveyGroupId: record.groupId, displayedSurveyId: record.surveyId });
  const getResultLink = record => generatePath(surveyPath, { ...params, page: 'results', surveyGroupId: record.groupId, displayedSurveyId: record.surveyId });

  const onSurveyCreated = (survey) => {
    refetch();
    history.push(getEditLink({ groupId: survey.languageParent.id, surveyId: survey.id }));
  };

  const surveyVersionLimit = getDataSourceLicenseSetting('surveyVersionLimit');
  const surveyLanguageLimit = getDataSourceLicenseSetting('surveyLanguageLimit');
  const canCreateSurvey = hasDataSourcePermission(CREATE_SURVEY);

  const actionsMenuItems = useCallback((record) => {
    if (!canEdit) return [];

    const canCreateVersion = !hasLicenseLimit(surveyVersionLimit) || record.children?.length < surveyVersionLimit;
    const canCreateTranslation = !hasLicenseLimit(surveyLanguageLimit) || record.translations?.length < surveyLanguageLimit - 1;
    const groupSurveys = dataSource?.surveyGroups.find(group => group.id === record.groupId)?.surveys || [];
    const selectedSurvey = groupSurveys.find(survey => survey.id === record.surveyId);
    const needsPrimaryDelegation = selectedSurvey?.primary && groupSurveys.some(survey => survey.id !== selectedSurvey.id && survey.version === selectedSurvey.version);
    const menuItems = [{
      key: 'preview',
      label: 'Preview',
      icon: <EyeOutlined />,
      onClick: () => surveyPreviewPopup.open(record.distributions[record.distributions.length - 1].id),
      visible: record.distributions?.length > 0,
    }, {
      key: 'rename-version',
      label: 'Rename',
      icon: <EditOutlined />,
      onClick: () => setRenameSurveyVersion({ surveyId: record.surveyId, surveyGroupId: record.group && record.groupId, title: record.title }),
    }, {
      key: 'stop-version',
      label: 'Stop',
      icon: <StopOutlined />,
      danger: true,
      onClick: () => stopSurvey({ variables: { id: record.surveyId } }),
      visible: record.status === DISTRIBUTION_STATUS.ACTIVE,
    }, {
      key: 'add-translation',
      label: (
        <>
          Add translation
          {' '}
          {!canCreateTranslation && <LicenseTag dataSourceId={dataSource.id} setting="surveyLanguageLimit" showTooltip />}
        </>
      ),
      icon: <PlusOutlined />,
      onClick: () => setAddTranslationId(record.surveyId),
      disabled: !canCreateTranslation,
      visible: !!record.primary,
    }, {
      key: 'add-version',
      label: (
        <>
          Copy to new version
          {' '}
          {!canCreateVersion && <LicenseTag dataSourceId={dataSource.id} setting="surveyVersionLimit" showTooltip />}
        </>
      ),
      icon: <PlusOutlined />,
      onClick: () => onCreateVersion(record.surveyId),
      disabled: !canCreateVersion,
      visible: !!record.primary,
    }, {
      key: 'make-primary',
      label: 'Make primary',
      icon: <UpCircleOutlined />,
      onClick: () => onMakePrimary(record.surveyId),
      visible: record.version === 'Translation',
    }, {
      key: 'migrate-survey',
      label: 'Migrate',
      icon: <ToTopOutlined />,
      onClick: () => setMigrateSurvey(record),
      visible: !!(isSuperUser && record.group && !record.versions.length),
    }, {
      key: 'delete-survey',
      label: needsPrimaryDelegation ? 'Make another survey primary before deleting' : <FormattedMessage {...globalMessages.delete} />,
      icon: <DeleteFilled />,
      onClick: () => onDeleteSurvey(record.surveyId),
      danger: true,
      disabled: needsPrimaryDelegation,
      visible: !record.responseCount && (record.group || !record.primary || !record.children),
    }];

    return menuItems;
  }, [dataSource, surveyPreviewPopup, canEdit, isSuperUser, surveyVersionLimit, surveyLanguageLimit]);

  const actions = (record) => {
    const actionButtons = [];
    actionButtons.push(
      <Link key="results" to={getResultLink(record)}>
        <Button icon={<BarChartOutlined />}>Results</Button>
      </Link>,
    );

    if (canEdit) {
      actionButtons.push(
        <Link key="edit" to={getEditLink(record)}>
          <Button icon={<EditOutlined />}>Edit</Button>
        </Link>,
      );
    } else {
      actionButtons.push(
        <Button key="preview" icon={<EyeOutlined />} onClick={() => surveyPreviewPopup.open(record.distributions[record.distributions.length - 1].id)}>Preview</Button>,
      );
    }

    return actionButtons;
  };

  const types = module === Module.UxSurveys ? [SurveyType.General, SurveyType.Feedback, SurveyType.Targeted] : [SurveyType.Cx];
  const settings = types.map(type => SURVEY_TYPE_LIMITS[type]);

  const columns = useMemo(() => [
    {
      dataIndex: 'title',
      title: <FormattedMessage {...globalMessages.title} />,
    },
    {
      dataIndex: 'version',
      title: <FormattedMessage {...globalMessages.version} />,
      align: 'center',
      render: renderVersion,
    },
    {
      dataIndex: 'languages',
      title: <FormattedMessage {...globalMessages.languages} />,
      render: (languages) => languages.map(l => <LanguageTag key={l.language} {...l} />),
      sorter: arraySorter('languages', 'languageName'),
      search: (languages, s) => languages.find(({ language, languageName }) => (languageName && languageName.toLowerCase().search(s) !== -1) || (language && language.toLowerCase().search(s) !== -1)),
    },
    {
      dataIndex: 'type',
      title: <FormattedMessage {...globalMessages.type} />,
      render: (type) => <SurveyTypeTag type={type} />,
      visible: types.length > 1,
    },
    {
      ...getDistributionMethodsColumn(),
      visible: false,
    },
    {
      dataIndex: 'viewCount',
      title: 'Views',
      type: 'number',
    },
    {
      dataIndex: 'responseCount',
      title: 'Responses',
      type: 'number',
    },
    {
      dataIndex: 'firstResponse',
      title: 'First response',
      type: 'relativeDate',
      visible: false,
    },
    {
      dataIndex: 'lastResponse',
      title: 'Last response',
      type: 'relativeDate',
      defaultSortOrder: 'descend',
    },
    {
      dataIndex: 'status',
      title: <FormattedMessage {...globalMessages.status} />,
      render: (status, record) => <SurveyGroupStatusTag surveyGroup={record} distributions={record.distributions} showTooltip />,
      align: 'center',
      width: 100,
    },
  ], [module]);

  const surveyUsage = useMemo(() => {
    const allUsage = dataSource?.currentUsage.find(u => u.setting === 'surveyLimit');
    if (allUsage?.limit != null) {
      return [allUsage];
    }
    return settings.map((setting) => {
      const usage = dataSource?.currentUsage.find(u => u.setting === setting);
      return usage?.limit != null ? usage : false;
    }).filter(Boolean);
  }, [types, dataSource]);

  return (
    <Page loading={loading}>
      <Page.Header
        title={MODULE_LABEL[module]}
        extra={(
          <Space key="usage" split={<Divider type="vertical" />}>
            {surveyUsage?.map(({ setting, current, limit }) => (
              <Space key={setting}>
                <SurveyLimitBadge limit={limit} count={current} />
                {surveyUsage.length > 1 ? <SurveyTypePluralLabel type={SURVEY_LIMIT_TYPES[setting]} /> : 'Surveys'}
              </Space>
            ))}
            {canCreateSurvey ? <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateSurvey(types?.length > 1 ? true : types[0])} className="create-survey">Create survey</Button> : null}
          </Space>
        )}
      />
      <Page.Content>
        <Table
          dataSource={surveys}
          loading={deleteSurveyLoading || stopSurveyLoading || createVersionLoading || makePrimaryLoading}
          columns={columns}
          actions={actions}
          actionsMenuItems={actionsMenuItems}
        />
      </Page.Content>
      <CreateSurvey
        open={!!createSurvey}
        onCancel={() => setCreateSurvey(false)}
        onDone={onSurveyCreated}
        dataSource={dataSource}
        type={createSurvey && createSurvey !== true ? createSurvey : null}
        possibleTypes={types}
      />
      <MigrateSurvey open={!!migrateSurvey} onCancel={() => setMigrateSurvey(false)} onDone={onEditDone} surveyGroup={migrateSurvey} website={dataSource?.website} />
      <AddTranslations surveyId={addTranslationId} open={!!addTranslationId} onCancel={() => setAddTranslationId(false)} onDone={onEditDone} />
      <RenameSurvey open={!!renameSurveyVersion} onCancel={() => setRenameSurveyVersion(false)} onDone={() => setRenameSurveyVersion(false)} {...renameSurveyVersion} />
    </Page>
  );
};

export default SurveyList;
